import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  BadgeMenuItemKey,
  BadgeCountValue,
  IDecisionedBadgeStatusCounts,
  IBadgeCountRequest,
  IBadgeCountResponse,
} from '../interface/badge-count.interface';

@Injectable({ providedIn: 'root' })
export class BadgeCountService {
  readonly dualApprovalUrl = `${environment.apiUrl}/manager/badgecount/dualapproval`;
  readonly decisionedUrl = `${environment.apiUrl}/manager/badgecount/decisioned`;

  constructor(private http: HttpClient) {}

  getDualApprovalCounts(menuItems: BadgeMenuItemKey[]): Observable<IBadgeCountResponse> {
    return this.postCounts(this.dualApprovalUrl, menuItems, false);
  }

  getDecisionedCounts(menuItems: BadgeMenuItemKey[]): Observable<IBadgeCountResponse> {
    return this.postCounts(this.decisionedUrl, menuItems, true);
  }

  private postCounts(
    url: string,
    menuItems: BadgeMenuItemKey[],
    decisionedCounts: boolean
  ): Observable<IBadgeCountResponse> {
    const request: IBadgeCountRequest = { menuItems };

    return this.http
      .post<IBadgeCountResponse>(url, request, {
        headers: { 'Skip-Loading': 'true' },
      })
      .pipe(
        map(response => {
          for (const key of menuItems) {
            const count = response?.counts?.[key];
            const isValid = decisionedCounts
              ? this.isDecisionedCount(count)
              : this.isBadgeCount(count);
            if (!isValid) {
              throw new Error(`Invalid or missing badge count for ${key}.`);
            }
          }

          return response;
        })
      );
  }

  private isBadgeCount(value: BadgeCountValue | undefined): value is number {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
  }

  private isDecisionedCount(
    value: BadgeCountValue | undefined
  ): value is IDecisionedBadgeStatusCounts {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    return (
      Number.isSafeInteger(value.approved) &&
      value.approved >= 0 &&
      Number.isSafeInteger(value.denied) &&
      value.denied >= 0 &&
      Number.isSafeInteger(value.approved + value.denied)
    );
  }
}
